import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Video, 
  VideoOff, 
  Clock, 
  Calendar, 
  User, 
  AlertCircle,
  Loader2,
  ArrowLeft,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const MeetingPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetingData, setMeetingData] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [meetingEnded, setMeetingEnded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/meeting/${bookingId}` } });
      return;
    }
    checkMeetingAccess();
  }, [bookingId, isAuthenticated]);

  useEffect(() => {
    // Update time remaining every second
    if (meetingData && accessStatus === 'active') {
      const interval = setInterval(() => {
        const now = new Date();
        const endTime = new Date(meetingData.meeting_end_time);
        const diff = endTime - now;
        
        if (diff <= 0) {
          setMeetingEnded(true);
          clearInterval(interval);
          if (jitsiApiRef.current) {
            jitsiApiRef.current.dispose();
          }
        } else {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [meetingData, accessStatus]);

  useEffect(() => {
    // Initialize Jitsi when access is granted
    if (accessStatus === 'active' && meetingData && jitsiContainerRef.current && !jitsiApiRef.current) {
      initializeJitsi();
    }
    
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
    };
  }, [accessStatus, meetingData]);

  const checkMeetingAccess = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/api/meeting/${bookingId}/access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Access denied');
        setAccessStatus('denied');
        return;
      }
      
      setMeetingData(data);
      setAccessStatus(data.access_status);
      
      if (data.access_status === 'early') {
        // Set up countdown to meeting start
        const startTime = new Date(data.meeting_start_time);
        const now = new Date();
        const diff = startTime - now;
        const minutes = Math.floor(diff / 60000);
        setTimeRemaining(`${minutes} minutes`);
      }
    } catch (err) {
      setError('Failed to verify meeting access');
      setAccessStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const initializeJitsi = () => {
    const domain = 'meet.jit.si';
    const options = {
      roomName: meetingData.meeting_room_id,
      parentNode: jitsiContainerRef.current,
      width: '100%',
      height: '100%',
      configOverwrite: {
        startWithAudioMuted: true,
        startWithVideoMuted: false,
        prejoinPageEnabled: false,
        disableDeepLinking: true,
        enableWelcomePage: false,
        enableClosePage: false,
        disableInviteFunctions: true,
        hideConferenceSubject: true,
        hideConferenceTimer: false,
        toolbarButtons: [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'hangup',
          'chat',
          'settings',
          'videoquality',
          'tileview'
        ],
        notifications: [],
        disableThirdPartyRequests: true,
        analytics: { disabled: true }
      },
      interfaceConfigOverwrite: {
        TOOLBAR_BUTTONS: [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'hangup',
          'chat',
          'settings',
          'videoquality',
          'tileview'
        ],
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        SHOW_BRAND_WATERMARK: false,
        BRAND_WATERMARK_LINK: '',
        SHOW_POWERED_BY: false,
        SHOW_PROMOTIONAL_CLOSE_PAGE: false,
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
        DISABLE_PRESENCE_STATUS: true,
        DISABLE_FOCUS_INDICATOR: true,
        DEFAULT_BACKGROUND: '#1a1a2e',
        TOOLBAR_ALWAYS_VISIBLE: true,
        MOBILE_APP_PROMO: false,
        HIDE_INVITE_MORE_HEADER: true,
        DISABLE_RINGING: true
      },
      userInfo: {
        displayName: meetingData.participant_name
      }
    };

    // Load Jitsi external API script
    if (!window.JitsiMeetExternalAPI) {
      const script = document.createElement('script');
      script.src = 'https://meet.jit.si/external_api.js';
      script.async = true;
      script.onload = () => {
        jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, options);
        setupJitsiEventListeners();
      };
      document.body.appendChild(script);
    } else {
      jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, options);
      setupJitsiEventListeners();
    }
  };

  const setupJitsiEventListeners = () => {
    if (!jitsiApiRef.current) return;
    
    jitsiApiRef.current.addListener('videoConferenceLeft', () => {
      navigate('/dashboard');
    });
    
    jitsiApiRef.current.addListener('readyToClose', () => {
      navigate('/dashboard');
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    return timeStr;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg">Verifying meeting access...</p>
        </div>
      </div>
    );
  }

  if (error || accessStatus === 'denied') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-gray-400 mb-6">{error || 'You do not have permission to access this meeting.'}</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-red-600 hover:bg-red-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accessStatus === 'early') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-gray-800 border-gray-700">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Meeting Not Started Yet</h2>
              <p className="text-gray-400">The meeting room will open 10 minutes before the scheduled time.</p>
            </div>
            
            <div className="bg-gray-700/50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">Session Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-300">
                  <User className="w-5 h-5 text-red-500" />
                  <span>With: <strong>{meetingData.other_participant_name}</strong></span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Calendar className="w-5 h-5 text-red-500" />
                  <span>{formatDate(meetingData.booking_date)}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Clock className="w-5 h-5 text-red-500" />
                  <span>{formatTime(meetingData.booking_time)} ({meetingData.duration} min)</span>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-gray-400 mb-2">Meeting starts in:</p>
              <p className="text-3xl font-bold text-yellow-500">{timeRemaining}</p>
              <Button 
                onClick={checkMeetingAccess} 
                className="mt-6 bg-gray-700 hover:bg-gray-600"
              >
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accessStatus === 'expired' || meetingEnded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="p-8 text-center">
            <VideoOff className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Session Ended</h2>
            <p className="text-gray-400 mb-6">This training session has concluded. Thank you for using TrainerList!</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-red-600 hover:bg-red-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active meeting
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Exit
            </Button>
            <div className="h-6 w-px bg-gray-700" />
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5 text-green-500" />
              <span className="text-white font-medium">Training Session</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-400">
              <User className="w-4 h-4" />
              <span className="text-sm">With: {meetingData.other_participant_name}</span>
            </div>
            <div className="h-6 w-px bg-gray-700" />
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span className="text-white font-mono">{timeRemaining}</span>
              <span className="text-gray-500 text-sm">remaining</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Jitsi Container */}
      <div className="flex-1 relative">
        <div 
          ref={jitsiContainerRef} 
          className="absolute inset-0"
          style={{ minHeight: 'calc(100vh - 60px)' }}
        />
      </div>
    </div>
  );
};

export default MeetingPage;
